import { useState } from 'react';
import { FormDialog } from '@nyce/admin-kit/console';
import { ChipGroup, Switch, TextField } from '@nyce/admin-kit/ui';
import { stats, type ErrorGroup } from '../../api/stats';
import { compiles, matches, muteRegex, withMute } from './mute';
import { statsError } from './shared';

/**
 * Mute an error group: an exclude filter with the group's regex in front of the log filters of the chosen
 * organizations (each saved through the collector's monitoring update, which audits it), optionally closing the
 * group's entries there. Unmute = remove the filter on the organization's monitoring tab.
 */
export function MuteGroupDialog({
  group,
  customerId,
  onClose,
  onDone,
}: {
  group: ErrorGroup;
  /** The page shows one organization: only that one is offered. */
  customerId?: number;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const orgs = [
    ...new Map(
      group.installations
        .filter((i) => customerId == null || i.customerId === customerId)
        .map((i) => [i.customerId, i.name || `Customer ${i.customerId}`])
    ),
  ];
  if (customerId != null && !orgs.length) orgs.push([customerId, `Customer ${customerId}`]);
  const [regex, setRegex] = useState(() => muteRegex(group.title));
  const [chosen, setChosen] = useState<string[]>(orgs.map(([id]) => String(id)));
  const [close, setClose] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const valid = compiles(regex);
  const sampleHit = group.sample ? matches(regex, group.sample) : null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    let muted = 0;
    let already = 0;
    try {
      for (const id of chosen.map(Number)) {
        const org = await stats.organization(id);
        const filters = withMute(org.filters, regex.trim());
        if (!filters) {
          already++;
          continue;
        }
        await stats.updateMonitoring(id, { filters });
        muted++;
      }
      if (close)
        for (const id of chosen.map(Number))
          await stats.errorBulk({ fingerprint: group.fingerprint, customerId: id, action: 'ackclose' });
      onDone(
        `Muted in ${muted} organization${muted === 1 ? '' : 's'}` +
          (already ? `, already muted in ${already}` : '') +
          (close ? '; its entries are closed.' : '.')
      );
      onClose();
    } catch (e) {
      setError(new Error(`${statsError(e)}${muted ? ` (muted in ${muted} before this)` : ''}`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormDialog
      open
      title="Mute this error group"
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Mute"
      canSubmit={!!regex.trim() && valid && chosen.length > 0}
      busy={busy}
      error={error}
      draft={{ regex, chosen, close }}
      formClassName="nyk-form sx-mute-form"
    >
      <p className="nyk-span2 nyk-ui-hint">
        Adds an exclude filter in front of each organization&apos;s log filters: the collector stops storing
        messages that match. Undo it by removing the filter on the organization&apos;s monitoring tab.
      </p>
      <div className="nyk-span2">
        <TextField
          label="Regex"
          name="regex"
          value={regex}
          onChange={(e) => setRegex(e.target.value)}
          className="nyk-mono"
          hint="Made from the group's title (variable parts generalized); .NET regex, first matching filter wins."
        />
        {!valid && <p className="nyk-text-red">Not a valid regular expression.</p>}
        {valid && sampleHit !== null && (
          <p className={sampleHit ? 'nyk-ui-hint' : 'nyk-text-red'} data-testid="mute-sample">
            {sampleHit ? 'Matches the sample message.' : 'Does not match the sample message.'}
          </p>
        )}
      </div>
      <div className="nyk-span2">
        <ChipGroup
          label="Organizations"
          options={orgs.map(([id, name]) => ({ value: String(id), label: name }))}
          value={chosen}
          onChange={setChosen}
        />
      </div>
      <div className="nyk-span2">
        <Switch checked={close} onChange={setClose} label="Also close the group's entries there" />
      </div>
    </FormDialog>
  );
}
